// file : src/common/services/apiClient.ts
import { getApiBaseUrl } from 'common/services/apiBaseUrl';
const apiBaseUrl = getApiBaseUrl();
export const API_URL = apiBaseUrl + '/v2';

import apiFetch from 'common/services/apiFetch';

/**
 * Forme retournée par les routes /select : id + un champ libellé dont le nom
 * varie selon l'entité (label, referenceNumber, name…).
 * Utiliser String(item.monChamp) côté appelant pour garantir le type string.
 */
export type SelectItem = { id: number; [key: string]: string | number };

async function request<T>(
	url: string,
	method: string,
	token: string,
	body?: Partial<T>
): Promise<T> {
	const res = await apiFetch(`${API_URL}${url}`, {
		method,
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
		body: body ? JSON.stringify(body) : undefined,
	});
	if (!res.ok) throw new Error(`Failed to ${method} ${url}`);
	return method === 'DELETE' ? (undefined as unknown as T) : res.json();
}

async function requestBlob(url: string, token: string): Promise<Blob> {
	const res = await apiFetch(url, {
		method: 'GET',
		cache: 'no-store',
		headers: { Authorization: `Bearer ${token}` },
	});
	if (!res.ok) throw new Error(`Failed to fetch blob from ${url}`);
	return res.blob();
}

type Paginated<T> = {
	data: T[];
	meta: { page: number; limit: number; total: number; pages: number };
};

// Le serveur doit toujours renvoyer { data, meta }. Ce cas ne devrait pas arriver :
// on évite le plantage de l'écran et on laisse une trace.
function paginatedResponseWithoutData<T>(
	url: string,
	body: unknown
): Paginated<T> {
	console.error(`Liste paginée sans tableau data : ${url}`, body);
	return {
		data: [],
		meta: { page: 1, limit: 0, total: 0, pages: 0 },
	};
}

export const apiResource = <T>(resource: string) => {
	const baseUrl = `/${resource}`;

	return {
		// CRUD générique
		list: (token: string) => request<T[]>(baseUrl, 'GET', token),
		listPaginated: (
			token: string,
			options?: {
				page?: number;
				limit?: number;
				sort?: string;
				order?: 'ASC' | 'DESC';
				filters?: Record<string, string>;
			}
		) => {
			const params = new URLSearchParams();
			if (options?.page) params.append('page', options.page.toString());
			if (options?.limit) params.append('limit', options.limit.toString());
			if (options?.sort) params.append('sort', options.sort);
			if (options?.order) params.append('order', options.order);
			if (options?.filters) {
				Object.entries(options.filters).forEach(([k, v]) =>
					params.append(`filters[${k}]`, v)
				);
			}
			const url = `${baseUrl}?${params.toString()}`;
			return request<Paginated<T> | { data?: T[] }>(url, 'GET', token).then(
				(body) => {
					if (Array.isArray(body?.data)) return body as Paginated<T>;
					return paginatedResponseWithoutData<T>(url, body);
				}
			);
		},

		get: (id: number, token: string) =>
			request<T>(`${baseUrl}/${id}`, 'GET', token),
		create: (data: Partial<T>, token: string) =>
			request<T>(baseUrl, 'POST', token, data),
		update: (id: number, data: Partial<T>, token: string) =>
			request<T>(`${baseUrl}/${id}`, 'PATCH', token, data),
		delete: (id: number, token: string) =>
			request<void>(`${baseUrl}/${id}`, 'DELETE', token),
		duplicate: (id: number, token: string) =>
			request<T>(`${baseUrl}/${id}/duplicate`, 'POST', token),

		/**
		 * Appel allégé vers /{resource}/select — ne retourne que id + le champ
		 * libellé de l'entité. À utiliser pour alimenter les listes déroulantes.
		 */
		listSelect: (token: string) =>
			request<SelectItem[]>(`${baseUrl}/select`, 'GET', token),

		// Fonctions spécifiques aux documents
		generatePdf: (id: number, token: string) =>
			request<{ documentId: number; path: string }>(
				`${baseUrl}/${id}/generate-pdf`,
				'POST',
				token
			),
		downloadPdf: (id: number, token: string) =>
			requestBlob(
				`${API_URL}${baseUrl}/${id}/download-pdf?t=${Date.now()}`,
				token
			),
		deletePdf: (id: number, token: string) =>
			request<void>(`${baseUrl}/${id}/delete-pdf`, 'DELETE', token),
	};
};
